import React, { useState, useEffect, useCallback, useRef } from "react";
import { Search, Star, Plus, Minus, Loader2, User, History, TrendingUp, TrendingDown, Image as ImageIcon, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { api, formatDate, formatDateTime } from "@/lib/api";
import Seo from "@/components/Seo";
import { StarDashLayout } from "@/pages/star/StarLayout";
import { STANDARDS } from "@/lib/standards";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Users, Clock, Inbox, Check, X, IndianRupee, Wallet, CalendarCheck, PhoneCall, Download, Flame } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import MyAttendance from "@/pages/star/MyAttendance";
import StudentAttendance from "@/pages/star/StudentAttendance";
import AttendanceRangeView from "@/pages/star/AttendanceRangeView";
import StudentFollowups from "@/pages/star/StudentFollowups";
import Messages, { useUnreadMessages } from "@/pages/star/Messages";
import { MessageCircle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { downloadReceiptPdf, emailReceipt, openProof } from "@/lib/receipt";
import { ExplanationField } from "@/components/ExplanationField";
import { ACCT_FILTERS, AccountBadge, CompletionBar, AccountPanel, AccountSummary } from "@/pages/star/StudentAccountInfo";
import CashHandover from "@/pages/star/CashHandover";
import MyScore from "@/pages/star/MyScore";
import ClassDiary from "@/pages/star/diary/ClassDiary";
import { Award, NotebookPen, Megaphone } from "lucide-react";
import TeacherMarketing from "@/pages/star/TeacherMarketing";
import { IncompleteProfiles } from "@/components/IncompleteProfiles";

const fetchMyScore = (month) => api.get("/teacher/scorecard", { params: { month } }).then((r) => r.data);
import { ReceiptShareDialog } from "@/pages/star/ReceiptShare";

function CollectFees() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [student, setStudent] = useState(null);
  const [detail, setDetail] = useState(null);
  const [selPeriods, setSelPeriods] = useState([]);
  const [looking, setLooking] = useState(false);
  const [form, setForm] = useState({ amount: "", period: "", mode: "cash", reference: "", proof: "", proofName: "", paperReceiptNumber: "", customAmount: "", splitCash: "", splitUpi: "" });
  const [token, setToken] = useState(() => (crypto.randomUUID ? crypto.randomUUID() : String(Date.now() + Math.random())));
  const [saving, setSaving] = useState(false);
  const [shareReceipt, setShareReceipt] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [scan, setScan] = useState(null);
  const [mine, setMine] = useState([]);
  const [board, setBoard] = useState(null);
  const loadBoard = useCallback(async () => { try { const { data } = await api.get("/fees/collect-board"); setBoard(data); } catch { /* noop */ } }, []);
  useEffect(() => { loadBoard(); }, [loadBoard]);

  const loadMine = useCallback(async () => { try { const { data } = await api.get("/fees/teacher/collections"); setMine(data); } catch { /* noop */ } }, []);
  useEffect(() => { loadMine(); }, [loadMine]);

  const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
  const monthLabel = (p) => { try { const [y, m] = p.split("-"); return new Date(Number(y), Number(m) - 1, 1).toLocaleString("en-IN", { month: "long", year: "numeric" }); } catch { return p; } };

  const loadDetail = async (sid) => { try { const { data } = await api.get(`/fees/teacher/student/${sid}`); setDetail(data); } catch { setDetail(null); } };
  const selectStudent = (s) => { setStudent(s); setResults([]); setSelPeriods([]); setForm((x) => ({ ...x, amount: "", period: "", reference: "", proof: "", proofName: "", paidAt: "", paperReceiptNumber: "" })); setScan(null); if (proofRef.current) proofRef.current.value = ""; setDetail(null); loadDetail(s.id); };

  const doSearch = async (e) => {
    e && e.preventDefault();
    if (!q.trim()) return;
    setLooking(true); setStudent(null); setDetail(null); setResults([]);
    try {
      const { data } = await api.get("/fees/teacher/search", { params: { q: q.trim() } });
      setResults(data);
      if (data.length === 0) toast.error("No assigned student matches that name or roll number");
      else if (data.length === 1) selectStudent(data[0]);
    } catch (err) { toast.error(err.response?.data?.detail || "Search failed"); }
    finally { setLooking(false); }
  };

  const proofRef = useRef(null);
  const extraRef = useRef(null);
  const [extras, setExtras] = useState([]);
  const addExtra = (e) => {
    const fl = e.target.files?.[0]; if (!fl) return;
    const r = new FileReader(); r.onload = () => {
      const dataUrl = r.result;
      setExtras((a) => [...a, { proof: dataUrl, name: fl.name, mode: form.mode, amount: "" }]);
      api.post("/fees/proof/extract", { proof: dataUrl, mode: form.mode, roll_number: student?.roll_number || "" }).catch(() => {});
    }; r.readAsDataURL(fl);
    if (extraRef.current) extraRef.current.value = "";
  };
  const runScan = async (dataUrl, mode) => {
    if (!dataUrl) return;
    setScanning(true); setScan(null);
    try {
      const amt = isMonthly ? (parseInt(form.customAmount, 10) || monthlyTotal) : (parseInt(form.amount, 10) || 0);
      const { data } = await api.post("/fees/proof/extract", { proof: dataUrl, mode: mode === "split" ? "upi" : mode, roll_number: student?.roll_number || "", amount: amt });
      setScan(data);
      const f = data.fields || {};
      setForm((x) => {
        const nx = { ...x };
        if (mode !== "cash") {
          if (f.reference?.value) nx.reference = String(f.reference.value);
          if (f.date?.combined) nx.paidAt = f.date.combined;
        } else if (f.paper_receipt_number?.value) {
          nx.paperReceiptNumber = String(f.paper_receipt_number.value);
        }
        if (!isMonthly && f.amount?.value && !nx.amount) nx.amount = String(f.amount.value);
        return nx;
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't read the photo automatically. Please enter the details manually.");
    } finally { setScanning(false); }
  };
  const onProof = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 9_000_000) { toast.error("Photo too large (max ~9MB). Please choose a smaller image."); return; }
    const r = new FileReader(); r.onload = () => { setForm((x) => ({ ...x, proof: r.result, proofName: f.name })); runScan(r.result, form.mode); }; r.readAsDataURL(f);
  };
  const removeProof = () => { setForm((x) => ({ ...x, proof: "", proofName: "" })); setScan(null); if (proofRef.current) proofRef.current.value = ""; };

  const isMonthly = !!detail?.is_monthly;
  const togglePeriod = (p) => setSelPeriods((a) => a.includes(p) ? a.filter((x) => x !== p) : [...a, p]);
  const monthlyTotal = (detail?.unpaid_months || []).filter((m) => selPeriods.includes(m.period)).reduce((sum, m) => sum + m.outstanding, 0);

  const submit = async (e) => {
    e.preventDefault();
    if (!student) { toast.error("Confirm a student first"); return; }
    const pmode = form.mode === "split" ? "upi" : form.mode;
    const proofs = [
      ...(form.proof ? [{ proof: form.proof, mode: pmode, amount: null }] : []),
      ...extras.map((x) => ({ proof: x.proof, mode: x.mode || pmode, amount: x.amount ? Number(x.amount) : null })),
    ];
    const proofsArg = proofs.length ? proofs : undefined;
    const explanation = (form.explanation || "").trim() || undefined;
    const payAmt = isMonthly ? (parseInt(form.customAmount, 10) || monthlyTotal) : (parseInt(form.amount, 10) || 0);
    const extra = {};
    if (isMonthly && parseInt(form.customAmount, 10) > 0 && parseInt(form.customAmount, 10) !== monthlyTotal) extra.custom_amount = parseInt(form.customAmount, 10);
    if (form.mode === "split") {
      const sc = parseInt(form.splitCash, 10) || 0, su = parseInt(form.splitUpi, 10) || 0;
      if (sc <= 0 || su <= 0) { toast.error("Enter both the cash and the UPI amount"); return; }
      if (sc + su !== payAmt) { toast.error(`Cash ${inr(sc)} + UPI ${inr(su)} = ${inr(sc + su)}, but the payment is ${inr(payAmt)}`); return; }
      if (!form.proof) { toast.error("Upload the UPI screenshot"); return; }
      Object.assign(extra, { split_cash: sc, split_upi: su });
    }
    let body;
    if (isMonthly) {
      if (selPeriods.length === 0) { toast.error("Select at least one month to collect"); return; }
      body = { ...extra, roll_number: student.roll_number, periods: selPeriods, mode: form.mode, reference: form.reference, proof: form.proof, proofs: proofsArg, paper_receipt_number: form.paperReceiptNumber || undefined, paid_at: form.paidAt || undefined, explanation, client_token: token };
    } else {
      const amt = parseInt(form.amount, 10) || 0;
      if (amt <= 0) { toast.error("Enter an amount"); return; }
      body = { ...extra, roll_number: student.roll_number, amount: amt, period: form.period, mode: form.mode, reference: form.reference, proof: form.proof, proofs: proofsArg, paper_receipt_number: form.paperReceiptNumber || undefined, paid_at: form.paidAt || undefined, explanation, client_token: token };
    }
    setSaving(true);
    try {
      const { data } = await api.post("/fees/collections", body);
      toast.success(data.message || "Saved");
      if (data.digital_receipt_no) {
        try {
          const r = await api.get(`/fees/collections/${data.id}/digital-receipt`);
          setShareReceipt({ no: r.data.no, image: r.data.image, amount: r.data.amount, mode: form.mode, student: student.name, std: student.student_class, bill: r.data.bill || "", date: new Date().toLocaleDateString("en-GB"), mobile: student.parent_contact });
        } catch { /* receipt still saved; can be reopened later */ }
      }
      setForm({ amount: "", period: "", mode: "cash", reference: "", proof: "", proofName: "", paidAt: "", paperReceiptNumber: "", customAmount: "", splitCash: "", splitUpi: "" });
      setExtras([]); setScan(null);
      if (proofRef.current) proofRef.current.value = "";
      setSelPeriods([]);
      setToken(crypto.randomUUID ? crypto.randomUUID() : String(Date.now() + Math.random()));
      setStudent((s) => ({ ...s, outstanding: data.outstanding ?? s.outstanding, pending_approval: data.pending_approval ?? s.pending_approval }));
      loadDetail(student.id);
      loadMine();
      loadBoard();
    } catch (err) { toast.error(err.response?.data?.detail || "Submission failed"); }
    finally { setSaving(false); }
  };

  const fmt = formatDateTime;
  const sc = (s) => ({ approved: "bg-green-600", pending: "bg-amber-500", rejected: "bg-red-600" }[s] || "bg-slate-500");
  const remaining = detail?.outstanding ?? student?.outstanding;

  return (
    <div className="max-w-xl mx-auto space-y-4" data-testid="teacher-collect-fees">
      <div className="rounded-2xl border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900" data-testid="collect-rules">
        <b>Cash / UPI / Cheque:</b> recorded and held as <b>pending admin approval</b>. The official receipt is issued only after admin approves. UPI/Cheque: add screenshot/UTR or cheque details.
      </div>
      {!student && board && (
        <div className="rounded-2xl border border-slate-200 bg-white p-3" data-testid="teacher-collect-board">
          <div className="flex items-center justify-between mb-2"><h3 className="font-bold text-slate-900 text-sm">Fees to collect</h3></div>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {board.rows.map((r) => (
              <div key={r.id} className="rounded-xl border border-slate-200 p-3 flex items-center justify-between gap-2" data-testid={`tboard-row-${r.roll_number}`}>
                <div className="min-w-0"><p className="font-semibold text-slate-900 truncate">{r.name} · {r.roll_number}</p><p className="text-xs text-slate-500">{r.student_class} · Now {inr(r.collect_now)} · Outstanding {inr(r.total_outstanding)}</p>{r.next && <p className="text-[11px] text-slate-400" data-testid={`tboard-next-${r.roll_number}`}>Next: {inr(r.next.amount)}{r.next.date ? ` by ${formatDate(r.next.date)}` : ""}</p>}</div>
                <div className="flex items-center gap-2 shrink-0"><Badge className={r.status === "overdue" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}>{r.status}</Badge><Button size="sm" className="bg-blue-900" onClick={() => selectStudent({ id: r.id, name: r.name, student_class: r.student_class, roll_number: r.roll_number, outstanding: r.total_outstanding })} data-testid={`tboard-collect-${r.roll_number}`}>Collect</Button></div>
              </div>
            ))}
            {board.rows.length === 0 && <p className="text-sm text-slate-500 py-4 text-center">Nothing to collect right now.</p>}
          </div>
        </div>
      )}
      <form onSubmit={doSearch} className="flex gap-2">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search assigned students by name or roll (e.g. PTG-0010)" data-testid="collect-roll-input" />
        <Button type="submit" className="bg-blue-900 hover:bg-blue-950" data-testid="collect-lookup-btn">{looking ? <Loader2 className="h-4 w-4 animate-spin" /> : "Search"}</Button>
      </form>

      {results.length > 0 && (
        <div className="space-y-2" data-testid="collect-results">
          {results.map((s) => (
            <button key={s.id} type="button" onClick={() => selectStudent(s)} className="w-full text-left rounded-xl border border-slate-200 bg-white hover:bg-sky-50 p-3 flex items-center justify-between" data-testid={`collect-result-${s.id}`}>
              <div><p className="font-semibold text-slate-900">{s.name}</p><p className="text-xs text-slate-500">{s.student_class} · Roll {s.roll_number}</p></div>
              <span className="font-bold text-red-600">{inr(s.outstanding)}</span>
            </button>
          ))}
        </div>
      )}

      <ReceiptShareDialog receipt={shareReceipt} onClose={() => setShareReceipt(null)} />
      {student && (
        <form onSubmit={submit} className="rounded-2xl bg-white border border-slate-200 p-5 space-y-4" data-testid="collect-form">
          <div className="rounded-xl bg-gradient-to-br from-blue-900 to-sky-700 text-white p-4 flex items-center justify-between">
            <div><p className="text-sky-200 text-xs uppercase tracking-widest">{student.student_class} · Roll {student.roll_number}</p><p className="text-xl font-extrabold" data-testid="collect-student-name">{student.name}</p></div>
            <div className="text-right"><p className="text-xs text-sky-200">Remaining</p><p className="text-2xl font-extrabold" data-testid="collect-remaining">{inr(remaining)}</p></div>
          </div>

          {isMonthly && (
            <div className="space-y-3" data-testid="collect-monthly">
              <div className="flex items-center justify-between text-sm flex-wrap gap-1">
                <span className="font-semibold text-slate-700" data-testid="collect-monthly-fee">Monthly Fee: {inr(detail.monthly_fee)}</span>
                {detail.first_unpaid && <span className="text-amber-600 font-medium" data-testid="collect-pending-from">Pending From: {monthLabel(detail.first_unpaid)}</span>}
              </div>
              {(detail.unpaid_months || []).length === 0 ? (
                <p className="text-sm text-green-600 font-medium" data-testid="collect-all-paid">All months are paid. Nothing pending.</p>
              ) : (<>
                <p className="text-xs text-slate-500">Select the month(s) to collect:</p>
                <div className="grid gap-2" data-testid="collect-month-list">
                  {detail.unpaid_months.map((m) => {
                    const active = selPeriods.includes(m.period);
                    return (
                      <button key={m.period} type="button" disabled={m.locked} onClick={() => togglePeriod(m.period)}
                        className={`flex items-center justify-between rounded-xl border p-3 text-left transition-colors ${m.locked ? "opacity-60 cursor-not-allowed border-slate-200 bg-slate-50" : active ? "border-blue-600 bg-blue-50" : "border-slate-200 bg-white hover:bg-slate-50"}`}
                        data-testid={`collect-month-${m.period}`}>
                        <span className="flex items-center gap-2">
                          <span className={`h-4 w-4 rounded border flex items-center justify-center ${active ? "bg-blue-600 border-blue-600" : "border-slate-300"}`}>{active && <Check className="h-3 w-3 text-white" />}</span>
                          <span className="font-medium text-slate-800">{monthLabel(m.period)}</span>
                          {m.locked && <Badge className="bg-amber-500 text-[10px] px-1.5">awaiting approval</Badge>}
                        </span>
                        <span className="font-semibold text-slate-900">{inr(m.outstanding)}</span>
                      </button>
                    );
                  })}
                </div>
                {selPeriods.length > 0 && (
                  <div className="rounded-xl bg-blue-900 text-white p-3 flex items-center justify-between" data-testid="collect-monthly-summary">
                    <span className="text-sm">{selPeriods.length} month{selPeriods.length > 1 ? "s" : ""} selected</span>
                    <span className="font-extrabold">Total payment {inr(monthlyTotal)}</span>
                  </div>
                )}
                {selPeriods.length > 0 && (
                  <div data-testid="collect-custom-amount-box">
                    <Label className="text-xs">Amount received (₹) — change only if the parent paid a different amount</Label>
                    <Input type="number" inputMode="numeric" placeholder={String(monthlyTotal)} value={form.customAmount} onChange={(e) => setForm((x) => ({ ...x, customAmount: e.target.value }))} data-testid="collect-custom-amount" />
                    {(() => {
                      const v = parseInt(form.customAmount, 10) || 0;
                      if (!v || v === monthlyTotal) return null;
                      return <p className={`text-[11px] mt-1 font-medium ${v < monthlyTotal ? "text-amber-700" : "text-blue-800"}`} data-testid="collect-custom-note">{v < monthlyTotal ? `Part payment — ${inr(monthlyTotal - v)} will remain due` : `${inr(v - monthlyTotal)} extra will go to the next month(s) and reduce that balance`}</p>;
                    })()}
                  </div>
                )}
              </>)}
            </div>
          )}

          {!isMonthly && (
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Amount (₹)</Label><Input type="number" inputMode="numeric" value={form.amount} onChange={(e) => setForm((x) => ({ ...x, amount: e.target.value }))} data-testid="collect-amount" /></div>
              <div><Label className="text-xs">Period / Month</Label><Input value={form.period} onChange={(e) => setForm((x) => ({ ...x, period: e.target.value }))} placeholder="LUMPSUM or 2026-06" data-testid="collect-period" /></div>
            </div>
          )}

          <div>
            <Label className="text-xs">Payment mode</Label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-1">
              {["cash", "cheque", "upi", "split"].map((m) => (
                <Button key={m} type="button" variant={form.mode === m ? "default" : "outline"} className={form.mode === m ? "bg-blue-900" : ""} onClick={() => { setForm((x) => ({ ...x, mode: m })); if (form.proof) runScan(form.proof, m); }} data-testid={`collect-mode-${m}`}>{m === "split" ? "CASH + UPI" : m.toUpperCase()}</Button>
              ))}
            </div>
          </div>
          {form.mode === "split" && (
            <div className="grid grid-cols-2 gap-3 rounded-xl border border-violet-200 bg-violet-50 p-3" data-testid="collect-split-box">
              <div><Label className="text-xs">Cash (₹)</Label><Input type="number" inputMode="numeric" value={form.splitCash} onChange={(e) => setForm((x) => ({ ...x, splitCash: e.target.value }))} data-testid="collect-split-cash" /></div>
              <div><Label className="text-xs">UPI (₹) — this child's share</Label><Input type="number" inputMode="numeric" value={form.splitUpi} onChange={(e) => setForm((x) => ({ ...x, splitUpi: e.target.value }))} data-testid="collect-split-upi" /></div>
              <p className="col-span-2 text-[11px] text-slate-600" data-testid="collect-split-sum">Cash + UPI = {inr((parseInt(form.splitCash, 10) || 0) + (parseInt(form.splitUpi, 10) || 0))} of {inr(isMonthly ? (parseInt(form.customAmount, 10) || monthlyTotal) : (parseInt(form.amount, 10) || 0))}. Upload the UPI screenshot below — the same family screenshot can be used for each sibling (needs admin approval).</p>
            </div>
          )}
          {form.mode !== "cash" && <div><Label className="text-xs">Reference / UTR / Cheque no.</Label><Input value={form.reference} onChange={(e) => setForm((x) => ({ ...x, reference: e.target.value }))} data-testid="collect-reference" /></div>}
          {form.mode !== "cash" && <div><Label className="text-xs">Actual payment date &amp; time (from proof / bank) — optional</Label><Input type="datetime-local" value={form.paidAt || ""} onChange={(e) => setForm((x) => ({ ...x, paidAt: e.target.value }))} data-testid="collect-paidat" /><p className="text-[11px] text-slate-400 mt-1">When the parent actually paid — not when you received the screenshot. Admin verifies this against the UTR.</p></div>}
          <div><Label className="text-xs">Payment proof — UPI screenshot / cheque photo (optional)</Label>
            <input ref={proofRef} type="file" accept="image/*" onChange={onProof} className="hidden" data-testid="collect-proof" />
            {!form.proof ? (
              <Button type="button" variant="outline" className="mt-1 w-full h-11" onClick={() => proofRef.current?.click()} data-testid="collect-proof-choose"><ImageIcon className="h-4 w-4 mr-1.5" /> Choose from gallery / files or take a photo</Button>
            ) : (
              <div className="mt-1 flex items-center gap-3 rounded-xl border border-slate-200 p-2" data-testid="collect-proof-preview">
                <img src={form.proof} alt="Payment proof preview" className="h-14 w-14 rounded-lg object-cover border border-slate-200" data-testid="collect-proof-thumb" />
                <div className="min-w-0 flex-1"><p className="text-xs font-medium text-slate-700 truncate" data-testid="collect-proof-name">{form.proofName || "Selected image"}</p><p className="text-[11px] text-green-600">Ready to upload with this payment</p></div>
                <div className="flex gap-1 shrink-0">
                  <Button type="button" size="sm" variant="outline" onClick={() => proofRef.current?.click()} data-testid="collect-proof-replace">Replace</Button>
                  <Button type="button" size="sm" variant="ghost" className="text-red-600" onClick={removeProof} data-testid="collect-proof-remove">Remove</Button>
                </div>
              </div>
            )}
            <p className="text-[11px] text-slate-400 mt-1">Pick an existing image from your Photo Library / Files, or take a new photo. Works on iPhone Safari &amp; Android.</p>
            <div className="mt-2" data-testid="collect-extra-proofs">
              <Label className="text-xs">More proofs (optional) — part payments or cash + bank</Label>
              <input ref={extraRef} type="file" accept="image/*" onChange={addExtra} className="hidden" data-testid="collect-extra-input" />
              {extras.map((x, idx) => (
                <div key={idx} className="mt-1 flex items-center gap-2 rounded-xl border border-slate-200 p-2" data-testid={`collect-extra-${idx}`}>
                  <img src={x.proof} alt="extra proof" className="h-12 w-12 rounded-lg object-cover border shrink-0" />
                  <select value={x.mode} onChange={(e) => setExtras((a) => a.map((it, i) => i === idx ? { ...it, mode: e.target.value } : it))} className="text-xs border rounded px-1 py-1" data-testid={`collect-extra-mode-${idx}`}>
                    <option value="upi">UPI</option><option value="cheque">CHEQUE</option><option value="cash">CASH</option><option value="bank">BANK</option>
                  </select>
                  <Input type="number" placeholder="₹ part" value={x.amount} onChange={(e) => setExtras((a) => a.map((it, i) => i === idx ? { ...it, amount: e.target.value } : it))} className="h-8 w-20 text-xs" data-testid={`collect-extra-amount-${idx}`} />
                  <Button type="button" size="sm" variant="ghost" className="text-red-600 shrink-0 px-2" onClick={() => setExtras((a) => a.filter((_, i) => i !== idx))} data-testid={`collect-extra-remove-${idx}`}>×</Button>
                </div>
              ))}
              <Button type="button" variant="outline" className="mt-1 w-full h-9" onClick={() => extraRef.current?.click()} data-testid="collect-extra-add"><ImageIcon className="h-4 w-4 mr-1.5" /> Add another proof image</Button>
            </div>
          </div>
          {form.proof && (
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 space-y-2" data-testid="proof-scan">
              {scanning ? (
                <p className="text-xs text-slate-600 flex items-center gap-2" data-testid="proof-scanning"><Loader2 className="h-4 w-4 animate-spin" /> Reading the photo…</p>
              ) : scan ? (<>
                <p className="text-xs font-semibold text-slate-700">Read from photo {scan.cached && <span className="text-slate-400 font-normal">(reused — no new scan)</span>}</p>
                {scan.mode_warning && <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2" data-testid="proof-mode-warning">{scan.mode_warning}</p>}
                {scan.duplicate && <div className="text-[11px] text-red-700 bg-red-50 border border-red-200 rounded-lg p-2" data-testid="proof-duplicate">Possible duplicate: reference <b>{scan.duplicate.reference}</b> already appears on {scan.duplicate.matches.length} payment(s){scan.duplicate.matches[0] ? ` — e.g. ${scan.duplicate.matches[0].student_name || scan.duplicate.matches[0].roll_number} · ${inr(scan.duplicate.matches[0].amount)}` : ""}. Please verify before submitting.</div>}
                <div className="space-y-1.5">
                  {Object.entries(scan.fields || {}).map(([k, f]) => {
                    const cls = f.status === "ok" ? "bg-green-100 text-green-700" : f.status === "review" ? "bg-amber-100 text-amber-700" : "bg-slate-200 text-slate-500";
                    const tag = f.status === "ok" ? "read from photo" : f.status === "review" ? "review" : "not found";
                    const label = { amount: "Amount", date: "Date/time", reference: "Reference", paper_receipt_number: "Paper receipt no." }[k] || k;
                    return (
                      <div key={k} className="flex items-start justify-between gap-2 text-xs" data-testid={`proof-field-${k}`}>
                        <div className="min-w-0"><span className="text-slate-500">{label}: </span><span className="font-medium text-slate-800">{f.value !== null && f.value !== undefined && f.value !== "" ? String(f.value) : "—"}</span>{f.note && <p className="text-[11px] text-slate-400">{f.note}</p>}</div>
                        <Badge className={`${cls} shrink-0`}>{tag}</Badge>
                      </div>
                    );
                  })}
                </div>
                <p className="text-[11px] text-slate-400">Reading the photo does not approve the payment. Values are pre-filled to save typing — please check them. The student and amount are never changed automatically.</p>
              </>) : null}
            </div>
          )}
          <ExplanationField key={token} value={form.explanation || ""} onChange={(v) => setForm((x) => ({ ...x, explanation: v }))} testidPrefix="collect-explanation" />
          <Button type="submit" disabled={saving || (isMonthly && selPeriods.length === 0)} className="w-full h-12 bg-green-600 hover:bg-green-700 text-base" data-testid="collect-submit">{saving ? <Loader2 className="h-5 w-5 animate-spin" /> : <><IndianRupee className="h-5 w-5 mr-1" /> {form.mode === "cash" ? "Collect & issue receipt" : "Submit for approval"}</>}</Button>
        </form>
      )}

      <div>
        <h3 className="font-bold text-slate-900 mb-2 flex items-center gap-2"><Wallet className="h-4 w-4" /> My recent submissions</h3>
        <div className="space-y-2" data-testid="collect-mine">
          {mine.length === 0 ? <p className="text-sm text-slate-500">No submissions yet.</p> : mine.slice(0, 20).map((c) => (
            <div key={c.id} className="rounded-xl border border-slate-200 bg-white p-3 text-sm" data-testid={`collect-mine-${c.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-slate-800 break-words">{c.student_name} · Roll {c.roll_number}</p>
                  <p className="text-xs text-slate-500 break-words">{inr(c.amount)} · {c.split ? `CASH ${inr(c.split.cash)} + UPI ${inr(c.split.upi)}` : c.mode.toUpperCase()} · {c.period || "—"} · {fmt(c.created_at)}{c.receipt_no ? ` · ${c.receipt_no}` : ""}</p>
                </div>
                <Badge className={`${sc(c.status)} shrink-0`}>{c.status}</Badge>
              </div>
              <div className="flex flex-wrap gap-2 mt-2.5">
                {c.has_proof && <Button size="sm" variant="outline" onClick={() => openProof(c.id)} data-testid={`collect-proof-view-${c.id}`}><ImageIcon className="h-4 w-4 mr-1" /> Proof</Button>}
                {c.status === "approved" && c.receipt_no && <>
                  <Button size="sm" variant="outline" onClick={() => downloadReceiptPdf(c.id, "teacher", c.receipt_no)} data-testid={`collect-receipt-pdf-${c.id}`}><Download className="h-4 w-4 mr-1" /> PDF</Button>
                  <Button size="sm" variant="outline" onClick={() => emailReceipt(c.id, "teacher")} data-testid={`collect-receipt-email-${c.id}`}><Mail className="h-4 w-4 mr-1" /> Email</Button>
                </>}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function StarRequests() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [amounts, setAmounts] = useState({});
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/teacher/star-requests");
      setRequests(data);
      const m = {}; data.forEach((r) => { m[r.id] = r.requested_stars; });
      setAmounts(m);
    } catch { toast.error("Could not load requests"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const decide = async (r, action) => {
    setBusy(r.id);
    try {
      if (action === "approve") {
        const n = parseInt(amounts[r.id], 10) || 0;
        if (n < 1) { toast.error("Enter stars to approve (at least 1)"); setBusy(null); return; }
        const { data } = await api.post(`/teacher/star-requests/${r.id}/approve`, { stars: n });
        toast.success(`Approved ${data.approved_stars}★ — new balance ${data.balance}`);
      } else {
        await api.post(`/teacher/star-requests/${r.id}/reject`);
        toast.success("Request rejected");
      }
      setRequests((list) => list.filter((x) => x.id !== r.id));
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Action failed");
      load();
    } finally { setBusy(null); }
  };

  const fmt = formatDateTime;

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-blue-900" /></div>;
  if (requests.length === 0) return (
    <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center text-slate-500" data-testid="teacher-no-requests">
      <Inbox className="h-8 w-8 mx-auto mb-2 text-slate-400" />
      No pending star requests from your students.
    </div>
  );

  return (
    <div className="space-y-3" data-testid="teacher-requests-list">
      <p className="text-xs text-slate-400">Only requests from students assigned to you appear here. You can change the star amount before approving.</p>
      {requests.map((r) => (
        <div key={r.id} className="rounded-2xl bg-white border border-slate-200 shadow-sm p-5" data-testid={`request-row-${r.id}`}>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex-1 min-w-0">
              <p className="font-bold text-slate-900">{r.student_name} <span className="text-slate-400 font-normal">· {r.student_class} · Roll {r.roll_number}</span></p>
              <p className="text-sm text-slate-700 mt-1">"{r.reason}"</p>
              <p className="text-xs text-slate-500 mt-1">Requested <span className="font-semibold text-amber-600">{r.requested_stars}★</span> · {fmt(r.created_at)}</p>
            </div>
            <div className="flex items-end gap-2">
              <div>
                <Label className="mb-1 block text-xs font-semibold text-slate-600">Approve stars</Label>
                <Input type="number" min="1" max="100" value={amounts[r.id] ?? r.requested_stars}
                  onChange={(e) => setAmounts((m) => ({ ...m, [r.id]: e.target.value }))}
                  className="w-24" data-testid={`request-stars-input-${r.id}`} />
              </div>
              <Button size="sm" onClick={() => decide(r, "approve")} disabled={busy === r.id} className="bg-green-600 hover:bg-green-700" data-testid={`request-approve-${r.id}`}>
                {busy === r.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Check className="h-4 w-4 mr-1" /> Approve</>}
              </Button>
              <Button size="sm" variant="outline" onClick={() => decide(r, "reject")} disabled={busy === r.id} className="text-red-600" data-testid={`request-reject-${r.id}`}>
                <X className="h-4 w-4 mr-1" /> Reject
              </Button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function MyStudents() {
  const [q, setQ] = useState("");
  const [students, setStudents] = useState([]);
  const [selected, setSelected] = useState(null);
  const [history, setHistory] = useState([]);
  const [delta, setDelta] = useState(1);
  const [reason, setReason] = useState("");
  const [sign, setSign] = useState(1);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [stdFilter, setStdFilter] = useState("all");
  const [acctFilter, setAcctFilter] = useState("all");

  const search = useCallback(async (term) => {
    setLoading(true);
    try {
      const { data } = await api.get("/teacher/students", { params: { q: term } });
      setStudents(data);
    } catch { toast.error("Could not load students"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { search(""); }, [search]);

  const loadHistory = async (sid) => {
    try { const { data } = await api.get(`/teacher/students/${sid}/transactions`); setHistory(data); }
    catch { setHistory([]); }
  };

  const selectStudent = (s) => { setSelected(s); setReason(""); setDelta(1); setSign(1); loadHistory(s.id); };

  const submit = async (e) => {
    e.preventDefault();
    if (!reason.trim()) { toast.error("A reason is required"); return; }
    const value = sign * Math.abs(parseInt(delta, 10) || 0);
    if (value === 0) { toast.error("Star change cannot be zero"); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/teacher/students/${selected.id}/stars`, { delta: value, reason: reason.trim() });
      toast.success(`Updated. New balance: ${data.balance}`);
      setSelected((s) => ({ ...s, star_balance: data.balance }));
      setStudents((list) => list.map((x) => x.id === selected.id ? { ...x, star_balance: data.balance } : x));
      setReason("");
      loadHistory(selected.id);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Update failed");
    } finally { setSaving(false); }
  };

  const fmt = formatDateTime;

  const acctTest = ACCT_FILTERS.find((f) => f.key === acctFilter)?.test;
  const visibleStudents = students.filter((s) => (stdFilter === "all" || s.student_class === stdFilter) && (!acctTest || acctTest(s)));
  const markRequested = () => { setSelected((s) => ({ ...s, reset_requested: true })); setStudents((l) => l.map((x) => x.id === selected.id ? { ...x, reset_requested: true } : x)); };
  const presentStandards = STANDARDS.filter((s) => students.some((x) => x.student_class === s));

  return (
    <div className="grid lg:grid-cols-5 gap-6">
      {/* Student list */}
      <div className="lg:col-span-2">
          <div className="mb-3"><IncompleteProfiles onOpen={(p) => { const s = students.find((x) => x.id === p.id); if (s) selectStudent(s); else { setQ(p.roll_number || p.name); search(p.roll_number || p.name); toast.info("Showing this student in the list below"); } }} /></div>
          <form onSubmit={(e) => { e.preventDefault(); search(q); }} className="flex gap-2 mb-2">
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, roll or class" data-testid="teacher-search-input" />
            <Button type="submit" className="bg-blue-900 hover:bg-blue-950" data-testid="teacher-search-btn"><Search className="h-4 w-4" /></Button>
          </form>
          <Select value={stdFilter} onValueChange={setStdFilter}>
            <SelectTrigger className="mb-2" data-testid="teacher-standard-filter"><SelectValue placeholder="Filter by standard" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="teacher-standard-all">All standards</SelectItem>
              {presentStandards.map((s) => <SelectItem key={s} value={s} data-testid={`teacher-standard-${s}`}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="flex flex-wrap gap-1.5 mb-2" data-testid="teacher-acct-filters">
            {ACCT_FILTERS.map((f) => (
              <button key={f.key} type="button" onClick={() => setAcctFilter(f.key)} className={`rounded-full px-2.5 py-1 text-xs font-semibold transition-colors ${acctFilter === f.key ? "bg-blue-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`} data-testid={`teacher-acct-filter-${f.key}`}>
                {f.label}{f.test ? ` (${students.filter(f.test).length})` : ""}
              </button>
            ))}
          </div>
          <p className="text-xs text-slate-400 mb-4">You can only see and manage students assigned to you by the admin.</p>
          {loading ? (
            <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-blue-900" /></div>
          ) : visibleStudents.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-8">{students.length === 0 ? "No students are assigned to you yet." : "No assigned students in this standard."}</p>
          ) : (
            <div className="space-y-2" data-testid="teacher-student-list">
              {visibleStudents.map((s) => (
                <button key={s.id} onClick={() => selectStudent(s)} data-testid={`teacher-student-${s.id}`}
                  className={`w-full text-left rounded-xl border p-3 flex items-center gap-3 transition-colors ${selected?.id === s.id ? "border-blue-900 bg-sky-50" : "border-slate-200 bg-white hover:bg-slate-50"}`}>
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-blue-900 text-white font-bold">{s.name?.charAt(0) || "?"}</div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-slate-900 truncate">{s.name}</p>
                    <p className="text-xs text-slate-500">{s.student_class} · Roll {s.roll_number}</p>
                    <div className="flex items-center gap-2 mt-1 flex-wrap"><CompletionBar pct={s.completion ?? 0} testid={`teacher-student-completion-${s.id}`} /><AccountBadge s={s} /></div>
                  </div>
                  <span className="flex items-center gap-1 font-bold text-amber-600"><Star className="h-4 w-4 fill-amber-400 text-amber-400" />{s.star_balance ?? 0}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Detail / action */}
        <div className="lg:col-span-3">
          {!selected ? (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center text-slate-500 h-full flex flex-col items-center justify-center" data-testid="teacher-empty-detail">
              <User className="h-10 w-10 text-slate-400 mb-2" />
              Select a student to award or adjust stars.
            </div>
          ) : (
            <div className="space-y-6" data-testid="teacher-detail">
              <div className="rounded-2xl bg-gradient-to-br from-blue-900 to-sky-700 text-white p-6 flex items-center justify-between">
                <div>
                  <p className="text-sky-200 text-xs uppercase tracking-widest font-semibold">{selected.student_class} · Roll {selected.roll_number}</p>
                  <p className="text-2xl font-extrabold mt-1">{selected.name}</p>
                </div>
                <p className="text-4xl font-extrabold flex items-center gap-2" data-testid="teacher-selected-balance"><Star className="h-8 w-8 text-amber-400 fill-amber-400" />{selected.star_balance ?? 0}</p>
              </div>

              <AccountPanel s={selected} onRequested={markRequested} />

              <form onSubmit={submit} className="rounded-2xl bg-white border border-slate-200 p-6 shadow-sm space-y-4" data-testid="teacher-stars-form">
                <div className="flex gap-2">
                  <Button type="button" onClick={() => setSign(1)} variant={sign === 1 ? "default" : "outline"} className={sign === 1 ? "bg-green-600 hover:bg-green-700 flex-1" : "flex-1"} data-testid="teacher-sign-add"><Plus className="h-4 w-4 mr-1" /> Add</Button>
                  <Button type="button" onClick={() => setSign(-1)} variant={sign === -1 ? "default" : "outline"} className={sign === -1 ? "bg-red-600 hover:bg-red-700 flex-1" : "flex-1"} data-testid="teacher-sign-subtract"><Minus className="h-4 w-4 mr-1" /> Subtract</Button>
                </div>
                <div>
                  <Label className="mb-1.5 block font-semibold text-slate-700">Number of Stars</Label>
                  <Input type="number" min="1" value={delta} onChange={(e) => setDelta(e.target.value)} data-testid="teacher-stars-amount" required />
                </div>
                <div>
                  <Label className="mb-1.5 block font-semibold text-slate-700">Reason (required)</Label>
                  <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} placeholder="e.g. Excellent performance in weekly test" data-testid="teacher-stars-reason" required />
                </div>
                <Button type="submit" disabled={saving} className="w-full bg-blue-900 hover:bg-blue-950" data-testid="teacher-stars-submit">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : `${sign === 1 ? "Add" : "Subtract"} ${Math.abs(parseInt(delta, 10) || 0)} star(s)`}
                </Button>
              </form>

              <div>
                <h3 className="flex items-center gap-2 font-bold text-slate-900 mb-3"><History className="h-5 w-5" /> History</h3>
                {history.length === 0 ? (
                  <p className="text-sm text-slate-500">No star activity yet.</p>
                ) : (
                  <div className="space-y-2" data-testid="teacher-history-list">
                    {history.map((t) => (
                      <div key={t.id} className="rounded-xl bg-white border border-slate-200 p-3 flex items-center gap-3 text-sm">
                        {t.delta > 0 ? <TrendingUp className="h-4 w-4 text-green-600 shrink-0" /> : <TrendingDown className="h-4 w-4 text-red-600 shrink-0" />}
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-slate-800 truncate">{t.reason}</p>
                          <p className="text-xs text-slate-500">{t.teacher_name} · {fmt(t.created_at)} · {t.balance_before}→{t.balance_after}</p>
                        </div>
                        <span className={`font-bold ${t.delta > 0 ? "text-green-600" : "text-red-600"}`}>{t.delta > 0 ? "+" : ""}{t.delta}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
  );
}

const TEACHER_TABS = [
  { value: "students", testid: "tab-my-students", label: "My Students", Icon: Users, tile: "bg-blue-100 text-blue-700" },
  { value: "messages", testid: "tab-messages", label: "Messages", Icon: MessageCircle, tile: "bg-violet-100 text-violet-700" },
  { value: "student-attendance", testid: "tab-student-attendance", label: "Student Attendance", Icon: CalendarCheck, tile: "bg-emerald-100 text-emerald-700" },
  { value: "attendance-log", testid: "tab-attendance-log", label: "Attendance Log", Icon: CalendarCheck, tile: "bg-teal-100 text-teal-700" },
  { value: "followups", testid: "tab-followups", label: "Follow-ups", Icon: PhoneCall, tile: "bg-amber-100 text-amber-700" },
  { value: "requests", testid: "tab-star-requests", label: "Star Requests", Icon: Inbox, tile: "bg-rose-100 text-rose-700" },
  { value: "fees", testid: "tab-collect-fees", label: "Collect Fees", Icon: IndianRupee, tile: "bg-sky-100 text-sky-700" },
  { value: "cash", testid: "tab-cash-handover", label: "Cash Handover", Icon: Wallet, tile: "bg-emerald-100 text-emerald-700" },
  { value: "attendance", testid: "tab-my-attendance", label: "My Attendance", Icon: Clock, tile: "bg-indigo-100 text-indigo-700" },
  { value: "score", testid: "tab-my-score", label: "My Score", Icon: Award, tile: "bg-yellow-100 text-yellow-700" },
  { value: "diary", testid: "tab-class-diary", label: "Class Diary", Icon: NotebookPen, tile: "bg-emerald-100 text-emerald-700" },
  { value: "marketing", testid: "tab-marketing", label: "PTG Marketing", Icon: Megaphone, tile: "bg-green-100 text-green-700" },
];

const navCardCls = "group relative flex h-auto w-full flex-col items-start gap-3 rounded-2xl border border-slate-200 bg-white p-4 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md data-[state=active]:border-transparent data-[state=active]:bg-gradient-to-br data-[state=active]:from-blue-900 data-[state=active]:to-sky-600 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:shadow-blue-900/25";

export default function TeacherDashboard() {
  const { user } = useAuth();
  const unread = useUnreadMessages();
  const [streak, setStreak] = useState(null);
  useEffect(() => { api.get("/teacher/attendance").then((r) => setStreak(r.data?.streak)).catch(() => {}); }, []);

  const hero = (
    <div className="mb-6 flex items-center gap-3 flex-wrap" data-testid="teacher-welcome">
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-blue-800 to-sky-600 text-white text-lg font-extrabold shadow-md shadow-blue-900/20">{(user?.name || user?.email || "T").charAt(0).toUpperCase()}</div>
      <div className="min-w-0">
        <p className="text-xs text-slate-500">Welcome back,</p>
        <p className="text-lg font-extrabold text-slate-900 leading-tight truncate">{user?.name || user?.email}</p>
      </div>
      {streak?.current > 0 && (
        <span className="sm:ml-auto inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-3 py-1.5 text-xs font-bold text-amber-700 ring-1 ring-amber-200" data-testid="header-streak">
          <Flame className="h-3.5 w-3.5 text-amber-500" /> {streak.current}-shift on-time streak
        </span>
      )}
      <AccountSummary />
    </div>
  );

  return (
    <StarDashLayout title="Teacher Portal" testid="teacher-dashboard" headerGradient hero={hero}>
      <Seo title="Teacher Portal" />
      <Tabs defaultValue="students">
        <TabsList className="mb-8 grid w-full grid-cols-2 sm:grid-cols-4 gap-3 h-auto bg-transparent p-0">
          {TEACHER_TABS.map(({ value, testid, label, Icon, tile }) => (
            <TabsTrigger key={value} value={value} data-testid={testid} className={navCardCls}>
              <span className={`flex h-10 w-10 items-center justify-center rounded-xl ${tile} transition-colors group-data-[state=active]:bg-white/20 group-data-[state=active]:text-white`}>
                <Icon className="h-5 w-5" />
              </span>
              <span className="text-sm font-bold leading-tight">{label}</span>
              {value === "messages" && unread > 0 && <span className="absolute top-3 right-3 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-blue-600 text-white text-[10px] font-bold group-data-[state=active]:bg-white group-data-[state=active]:text-blue-700" data-testid="messages-tab-badge">{unread}</span>}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="students"><MyStudents /></TabsContent>
        <TabsContent value="messages"><Messages scope="teacher" /></TabsContent>
        <TabsContent value="student-attendance"><StudentAttendance /></TabsContent>
        <TabsContent value="attendance-log"><div className="max-w-4xl"><h2 className="text-lg font-bold text-slate-900 mb-1">Student attendance history</h2><p className="text-sm text-slate-500 mb-4">Check any of your assigned students' attendance for a date range.</p><AttendanceRangeView pickerSource="teacher" /></div></TabsContent>
        <TabsContent value="followups"><StudentFollowups /></TabsContent>
        <TabsContent value="requests"><StarRequests /></TabsContent>
        <TabsContent value="fees"><CollectFees /></TabsContent>
        <TabsContent value="cash"><CashHandover /></TabsContent>
        <TabsContent value="attendance"><MyAttendance /></TabsContent>
        <TabsContent value="score"><MyScore fetcher={fetchMyScore} /></TabsContent>
        <TabsContent value="diary"><ClassDiary /></TabsContent>
        <TabsContent value="marketing"><TeacherMarketing /></TabsContent>
      </Tabs>
    </StarDashLayout>
  );
}
